import { API, Auth } from "aws-amplify";

import * as mutations from "../../graphql/mutations";
import * as queries from "../../graphql/queries";
import * as customQueries from "../../graphql/customQueries";
import {
  UserInvitation,
  GetUserInvitationQuery,
  CreateUserInvitationInput,
  CreateUserInvitationMutation,
  ListUserInvitationsQuery,
  DeleteUserInvitationMutation,
  DeleteUserInvitationInput,
  ModelUserInvitationConnection,
  EULAText,
  SearchUserInvitationsQueryVariables,
  SearchUserInvitationsQuery,
  SearchableUserInvitationConnection,
  UserInvitationsByEmailLowerQuery,
  ValidateBulkUploadQuery,
  ValidateBulkUploadInvitation
} from "../../API";
import {
  genericQuery,
  listQueryAutoPaginated,
  searchQuery
} from "../api/apiUtils";
import { listUserInvitationsByProgramIds } from "../../queries/nongenedQueries";

export const fetchUserInvitationByEmail = async (
  email: string,
  unauthed?: boolean
) => {
  const predicate = { emailLower: email.toLowerCase() };

  const results = await listQueryAutoPaginated<
    UserInvitationsByEmailLowerQuery,
    UserInvitation,
    ModelUserInvitationConnection
  >(
    unauthed
      ? customQueries.getUserInvitationsByEmailUnauthed
      : queries.userInvitationsByEmailLower,
    "userInvitationsByEmailLower",
    "items",
    predicate,
    unauthed
  );
  if (results && results.length > 0) {
    const resultToReturn = results[0];
    return resultToReturn;
  }
  return null;
};

export const fetchAllUserInvitations = async (predicate?: object) => {
  return await listQueryAutoPaginated<
    ListUserInvitationsQuery,
    UserInvitation,
    ModelUserInvitationConnection
  >(queries.listUserInvitations, "listUserInvitations", "items", predicate);
};

export async function fetchUserInvitation(id: string, unauthed?: boolean) {
  const predicate = { id: id };
  return await genericQuery<GetUserInvitationQuery, UserInvitation>(
    unauthed
      ? customQueries.getUserInvitationUnauthed
      : queries.getUserInvitation,
    "getUserInvitation",
    predicate,
    unauthed
  );
}

export const fetchUserInvitationsByProgramIds = async (
  programIds: string[]
) => {
  if (!programIds?.length) {
    return [];
  }
  const predicate =
    programIds.length === 1
      ? { programId: programIds[0] }
      : { programIds };
  return await listQueryAutoPaginated<
    ListUserInvitationsQuery,
    UserInvitation,
    ModelUserInvitationConnection
  >(
    listUserInvitationsByProgramIds(programIds),
    "listUserInvitations",
    "items",
    predicate
  );
};

export const createUserInvitation = async (
  input: CreateUserInvitationInput
): Promise<UserInvitation | null> => {
  const predicate = { input: input };
  return await genericQuery<CreateUserInvitationMutation, UserInvitation>(
    mutations.createUserInvitation,
    "createUserInvitation",
    predicate
  );
};

export const deleteUserInvitation = async (
  input: DeleteUserInvitationInput
): Promise<UserInvitation | null> => {
  const predicate = { input: input };
  return await genericQuery<DeleteUserInvitationMutation, UserInvitation>(
    mutations.deleteUserInvitation,
    "deleteUserInvitation",
    predicate
  );
};

export const fetchEulaText = async (): Promise<EULAText | null> => {
  try {
    // Use REST API endpoint /eula-texts which returns the list ordered by createdAt desc
    const response = await API.get("simpl-api", "eula-texts", {});
    
    if (response?.eulatexts && Array.isArray(response.eulatexts) && response.eulatexts.length > 0) {
      // The endpoint already returns results ordered by createdAt descending
      // Return the first (newest) item
      const newest = response.eulatexts[0];
      return {
        id: newest.id,
        formattedText: newest.formattedText,
        createdAt: newest.createdAt,
        updatedAt: newest.updatedAt
      } as EULAText;
    }
    return null;
  } catch (error) {
    console.error("Error fetching EULA text via REST API:", error);
    throw error;
  }
};

export type ValidateBulkUploadResult = {
  success: boolean;
  invitations?: Array<{
    requestId?: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    programId?: string;
    npi?: string | number;
    simplLearnId?: string;
    pgy?: string | number;
    traineeType?: string;
    jobTitle?: string;
    errors?: Array<{ field: string; error: string }>;
  }>;
  error?: string;
}

export const fetchValidateBulkUpload = async (
  invitations: ValidateBulkUploadInvitation[]
): Promise<ValidateBulkUploadResult | null> => {
  const response = await API.post("simpl-api", "validate-bulk-upload", { 
    body: { invitations } 
  });
  return response as ValidateBulkUploadResult;
}

export interface CreateAuthAccountInput {
  email: string;
  password: string;
  permissions: string;
}
export async function createCognitoAccount(input: CreateAuthAccountInput) {
  const signupProps = {
    // This probably won't be needed after the other changes, but just to be sure.
    username: input.email.toLowerCase(),
    password: input.password,
    attributes: {
      email: input.email,
      "custom:permissions": input.permissions
    }
  };
  const { user } = await Auth.signUp({
    ...signupProps,
    autoSignIn: {
      enabled: true
    }
    // DEV NOTE: Auto-sign in will be handled in a future ticket.
    // autoSignIn: { enabled: true }
  });
  return user;
}

export interface ConfirmCognitoAccountInput {
  username: string;
  code: string;
}

export async function confirmCognitoAccount(input: ConfirmCognitoAccountInput) {
  const { username, code } = input;
  return await Auth.confirmSignUp(username, code);
}

export const resendConfirmationCode = async (username: string) => {
  return await Auth.resendSignUp(username);
};

export const resendSignUp = async (username: string) => {
  return await Auth.resendSignUp(username);
};

export const searchUserInvitations = async (
  predicate: SearchUserInvitationsQueryVariables
) => {
  const results = await searchQuery<
    SearchUserInvitationsQuery,
    SearchableUserInvitationConnection
  >(queries.searchUserInvitations, "searchUserInvitations", predicate);
  return results;
};
